"use server";

import { and, eq } from "drizzle-orm";

import { db } from "@/db";
import { profileLinks, reports, users } from "@/db/schema";
import { emailSpecTable, emailWrap } from "@/lib/email-theme";
import { sendMail } from "@/lib/mail";
import { env } from "@/env";
import { requireUser } from "@/server/auth/session";

export type ReportState = { error: string } | { ok: true } | null;

/**
 * Ported from `reports.store` (ReportController + SubmitReportRequest) — a
 * signed-in visitor reports a profile or one of its links; the row lands in
 * the moderation queue. Admin notification ported from NewReportNotification.
 */
export async function submitReport(
  _prev: ReportState,
  formData: FormData,
): Promise<ReportState> {
  const reporter = await requireUser();
  const reporterId = Number(reporter.id);

  const subjectType = formData.get("subject_type");
  const subjectId = Number(formData.get("subject_id"));
  const reason = String(formData.get("reason") ?? "").trim();

  if (subjectType !== "user" && subjectType !== "link") {
    return { error: "Pick what you're reporting." };
  }
  if (!Number.isInteger(subjectId) || subjectId <= 0) {
    return { error: "That couldn't be found." };
  }
  if (reason.length < 1 || reason.length > 1000) {
    return { error: "Add a reason (up to 1000 characters)." };
  }

  // Resolve the subject's owner the way SubmitReportRequest::withValidator does.
  const ownerId =
    subjectType === "user"
      ? (
          await db
            .select({ id: users.id })
            .from(users)
            .where(eq(users.id, subjectId))
        )[0]?.id
      : (
          await db
            .select({ userId: profileLinks.userId })
            .from(profileLinks)
            .where(eq(profileLinks.id, subjectId))
        )[0]?.userId;

  if (ownerId == null) {
    return { error: "That couldn't be found." };
  }
  if (ownerId === reporterId) {
    return { error: "You can't report your own profile or links." };
  }

  await db.insert(reports).values({
    reporterId,
    subjectType,
    subjectId,
    reason,
    status: "open",
  });

  // Notify all admins (ported from NewReportNotification).
  notifyAdmins(subjectType, subjectId, reason, reporterId).catch((err) =>
    console.error("Admin report notification failed:", err),
  );

  return { ok: true };
}

/** Send an email to every non-suspended admin about a new report. */
async function notifyAdmins(
  subjectType: string,
  subjectId: number,
  reason: string,
  reporterId: number,
) {
  const admins = await db
    .select({ id: users.id, name: users.name, handle: users.handle, email: users.email })
    .from(users)
    .where(and(eq(users.isAdmin, true), eq(users.isSuspended, false)));

  if (!admins.length) return;

  // Resolve subject label.
  let subjectLabel: string;
  if (subjectType === "user") {
    const row = await db
      .select({ handle: users.handle })
      .from(users)
      .where(eq(users.id, subjectId))
      .limit(1);
    subjectLabel = row[0] ? `Profile: @${row[0].handle}` : "Deleted profile";
  } else {
    const row = await db
      .select({ label: profileLinks.label })
      .from(profileLinks)
      .where(eq(profileLinks.id, subjectId))
      .limit(1);
    subjectLabel = row[0] ? `Link: ${row[0].label}` : "Deleted link";
  }

  // Resolve reporter handle.
  const reporterRow = await db
    .select({ handle: users.handle })
    .from(users)
    .where(eq(users.id, reporterId))
    .limit(1);
  const reporterHandle = reporterRow[0]?.handle ?? "unknown";

  const reasonTruncated = reason.length > 200 ? `${reason.slice(0, 197)}…` : reason;
  const adminUrl = `${env.APP_URL}/admin/reports`;

  const html = emailWrap({
    subject: "New report on Collr",
    context: "REPORT",
    body: `<p style="color:#a9adb4;font-size:15px;line-height:1.65em;margin:0 0 16px;">A new content report has been submitted.</p>${emailSpecTable([["REPORTED", subjectLabel], ["REASON", reasonTruncated], ["REPORTER", `@${reporterHandle}`]])}`,
    button: { label: "REVIEW REPORT", url: adminUrl },
  });
  const text = `New report\nReported: ${subjectLabel}\nReason: ${reasonTruncated}\nReported by: @${reporterHandle}\nReview: ${adminUrl}`;

  for (const admin of admins) {
    await sendMail({
      to: admin.email,
      subject: `New report on Collr`,
      text,
      html,
    });
  }
}
